import { Component, ChangeDetectionStrategy, ChangeDetectorRef, inject, signal, computed } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IrisDMSFileI } from '@iris/common/modules/dms/models/IrisDMSFile';
import { IrisDmsFilesSandbox } from '@iris/common/modules/dms/sandbox/dms-files.sandbox';
import { finalize, take, tap } from 'rxjs/operators';
import { OnInit } from '@angular/core';
import { IrisDmsFoldersSandbox } from '../../../sandbox/dms-folders.sandbox';
import { IrisDmsLinkedFilesStore } from '../../../store/linked-files.signal-store';
import { IrisDmsPermissionsSandbox } from '../../../sandbox/dms-permissions.sandbox';
import { IrisLoaderModule } from '@iris/common/modules/loader';
import { IrisDmsFileLinkCardComponent } from '@iris/modules/dms/components/dms-info-sidebar/dms-file-links/dms-file-link-card/dms-file-link-card.component';
import { IrisDmsFormsLinkCardComponent } from '@iris/modules/dms/components/dms-info-sidebar/dms-file-links/dms-forms-link-card/dms-forms-link-card.component';
import { StrakitButtonComponent } from '@ngx-strakit/cdk/controls';
import {
  ConfirmByKeypressDirective,
  SkModalBase,
  SkModalContentComponent,
  StrakitButtonPrefixDirective,
} from '@ngx-strakit/cdk';

@Component({
  selector: 'iris-dms-file-delete-confirm-modal',
  imports: [
    TranslatePipe,
    SkModalContentComponent,
    IrisLoaderModule,
    IrisDmsFileLinkCardComponent,
    IrisDmsFormsLinkCardComponent,
    ConfirmByKeypressDirective,
    StrakitButtonComponent,
    StrakitButtonPrefixDirective,
  ],
  templateUrl: './file-delete-confirm-modal.component.html',
  styleUrls: ['./file-delete-confirm-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrisDmsFileDeleteConfirmModalComponent extends SkModalBase<IrisDmsFileDeleteConfirmModalComponent, void> implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly dmsFilesSandbox = inject(IrisDmsFilesSandbox);
  private readonly dmsFoldersSandbox = inject(IrisDmsFoldersSandbox);
  private readonly dmsPermissionsSandbox = inject(IrisDmsPermissionsSandbox);
  private readonly changeDetector = inject(ChangeDetectorRef);
  readonly data = inject<{
    file: IrisDMSFileI;
    versionId: string;
  }>(MAT_DIALOG_DATA);

  private readonly linkedFilesStore = inject(IrisDmsLinkedFilesStore);

  readonly file = this.data.file;
  readonly versionId = this.data.versionId;
  readonly folder = computed(() => this.dmsFoldersSandbox.getFolderById(this.file.parentId)());
  public loading = signal(true);

  readonly linkedFiles = this.linkedFilesStore.files;

  private readonly hasLinkedFiles = this.linkedFilesStore.hasLinkedFiles;
  private isFileAttachedToForm = false;
  private readonly selectedFolder = this.dmsFoldersSandbox.selectedFolder;

  get isAdmin(): boolean {
    return this.dmsPermissionsSandbox.isAdmin;
  }

  ngOnInit(): void {
    this.checkIfFileAttachedToForms();
    this.linkedFilesStore.loadLinkedFiles(this.file.id);
  }

  get removeText(): string {
    const formsRemoveMessage = this.isAdmin ? 'text.dms.RemoveFileAttachedToForms' : 'text.dms.RemoveFileAttachedToFormsInfo';
    switch (true) {
      case this.selectedFolder()?.isRecentFilesFolder:
        return this.translateService.instant('text.dms.RecentFileRemoveConfirm', { fileName: this.file.name });
      case !!this.versionId:
        return this.translateService.instant('text.dms.FileVersionRemoveConfirm');
      case this.hasLinkedFiles():
        return this.translateService.instant('text.dms.RemoveFileWithLink', { fileName: this.file.name });
      case this.isFileAttachedToForm:
        return this.translateService.instant(formsRemoveMessage, { fileName: this.file.name });
      case this.folder()?.isSystem || this.folder()?.isTrashBin:
        return this.translateService.instant('text.dms.SystemFileRemoveConfirm', { fileName: this.file.name });
      default:
        return this.translateService.instant('text.dms.FileRemoveConfirmNewDms', { fileName: this.file.name });
    }
  }

  removeFile(): void {
    if (!this.isAdmin && this.isFileAttachedToForm) {
      this.closeWithoutResult();
      return;
    }
    this.closeWithResult(null);
  }

  checkIfFileAttachedToForms(): void {
    if (this.file?.id) {
      this.loading.set(true);
      this.dmsFilesSandbox.isFileAttachedToForm$(this.file.id).pipe(
        take(1),
        tap(isFileAttachedToForm => {
          this.isFileAttachedToForm = isFileAttachedToForm;
          this.changeDetector.markForCheck();
        }),
        finalize(() => this.loading.set(false)),
      ).subscribe();
    }
  }
}
